/**
 * PublicTabNav — öffentliche Tab-Leiste für anonyme Besucher, im Look der
 * eingeloggten App-Navigation. Steht auf Startseite, Preisseite, den beiden
 * Produktseiten, „Über uns" und den Community-Backtests (jeweils direkt unter
 * dem PublicHeader).
 *
 * Tabs:
 *   - Watchlist             → /:land/:sprache/watchlist
 *   - Backtest              → /:land/:sprache/portfolio-backtester
 *   - Monte Carlo           → /:land/:sprache/monte-carlo-simulation
 *   - Für wen ▾             → Menü mit den vier Zielgruppenseiten (seit 07.10.2026)
 *   - Preise                → /:land/:sprache/pricing
 *   - Community-Backtests   → /:land/:sprache/community-backtests
 *
 * Reihenfolge: erst die Werkzeuge (wie in der App), dann für wen, dann was es
 * kostet, ganz rechts die Community-Backtests (seit 09.10.2026). „Für wen“ ist EIN Reiter mit Menü statt vier Reitern — am Telefon
 * passen nicht mehr als sechs in die eine Zeile, und Werkzeuge und Zielgruppen
 * sind zwei Arten von Navigation. Das Menü ist eine feste Fläche, kein Glas
 * (höchstens zwei bzw. drei Glasflächen je Seite); es schliesst bei Klick
 * daneben, Escape und Seitenwechsel.
 *
 * Watchlist, Backtest und Monte Carlo führen Gäste auf die Produktseiten der
 * drei Bereiche (Watchlist seit 07.10.2026, die anderen seit 04.10.2026) — so wechselt man oben mit einem Klick zwischen
 * ihnen. Vorher waren es gesperrte Reiter mit Anmelde-Hinweis; den braucht es
 * nicht mehr, denn beide Seiten haben „Kostenlos starten" und „Preise ansehen".
 * Die Startseite deckt beides ab und markiert keinen der beiden.
 *
 * Für eingeloggte Nutzer rendert die Komponente nichts — die haben die volle
 * App-Navigation (Layout.jsx) mit Backtest und Monte Carlo; die Produktseiten
 * schicken sie ohnehin in die App.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { BarChart3, Bookmark, ChevronDown, Dices, Table, Tag, Users } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useLanguage } from '../hooks/useLanguage';
import { einzigeFassung } from '../../app/_seo/seiten';
import { useTranslation } from '../hooks/useTranslation';

// Die Zielgruppenseiten im Menü „Für wen“ — je Seite Titel und ein Satz.
const ZIELGRUPPEN = [
  { pfad: '/depot-check', titelKey: 'public_nav.fuer_wen.depot_titel', titelDe: 'DIY-Anlegerinnen und -Anleger',
    textKey: 'public_nav.fuer_wen.depot_text', textDe: 'Depot-Check: Wie hätte dein Depot abgeschnitten?' },
  { pfad: '/fire', titelKey: 'public_nav.fuer_wen.fire_titel', titelDe: 'FIRE',
    textKey: 'public_nav.fuer_wen.fire_text', textDe: 'Finanzielle Freiheit, früh in Rente' },
  { pfad: '/altersvorsorgedepot-rechner', titelKey: 'public_nav.fuer_wen.av_titel', titelDe: 'Altersvorsorge',
    textKey: 'public_nav.fuer_wen.av_text', textDe: 'Altersvorsorgedepot: Förderung berechnen' },
  { pfad: '/entnahmeplan', titelKey: 'public_nav.fuer_wen.entnahme_titel', titelDe: 'Ruhestand & Entnahme',
    textKey: 'public_nav.fuer_wen.entnahme_text', textDe: 'Hätte dein Depot die Entnahmen getragen?' },
  { pfad: '/fuer-berater', titelKey: 'public_nav.fuer_wen.berater_titel', titelDe: 'Finanzberaterinnen und -berater',
    textKey: 'public_nav.fuer_wen.berater_text', textDe: 'Portfolios für jeden Mandanten' },
];

export default function PublicTabNav() {
  const { user } = useAuth();
  const { getLocalizedPath, country, language } = useLanguage();
  const { t } = useTranslation();
  const location = useLocation();
  const [menuOffen, setMenuOffen] = useState(false);
  const menuRef = useRef(null);
  const menuId = useId();

  // Seitenwechsel schliesst das Menü.
  useEffect(() => { setMenuOffen(false); }, [location.pathname]);

  // Klick daneben und Escape schliessen es.
  useEffect(() => {
    if (!menuOffen) return undefined;
    const beiKlick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOffen(false);
    };
    const beiTaste = (e) => { if (e.key === 'Escape') setMenuOffen(false); };
    document.addEventListener('mousedown', beiKlick);
    document.addEventListener('keydown', beiTaste);
    return () => {
      document.removeEventListener('mousedown', beiKlick);
      document.removeEventListener('keydown', beiTaste);
    };
  }, [menuOffen]);

  // Eingeloggte Nutzer haben die volle App-Navigation — keine öffentliche Leiste.
  if (user) return null;

  const pfad = location.pathname;
  // Was es nur in einer Fassung gibt (`NUR_IN`, Altersvorsorgedepot), steht nur dort im Menü.
  const zielgruppen = ZIELGRUPPEN.filter((z) => {
    const einzige = einzigeFassung(z.pfad);
    return !einzige || (einzige.land === country && einzige.code === language);
  });
  const zielgruppeAktiv = zielgruppen.some((z) => pfad.endsWith(z.pfad));
  const reiter = [
    { to: getLocalizedPath('/watchlist'), icon: Bookmark,
      label: t('nav.watchlist', 'Watchlist'),
      aktiv: pfad.endsWith('/watchlist') },
    { to: getLocalizedPath('/portfolio-backtester'), icon: Table,
      label: t('nav.portfolio_simulation', 'Backtest'),
      aktiv: pfad.endsWith('/portfolio-backtester') },
    { to: getLocalizedPath('/monte-carlo-simulation'), icon: Dices,
      label: t('nav.monte_carlo', 'Monte Carlo'),
      aktiv: pfad.endsWith('/monte-carlo-simulation') },
  ];
  const preise = { to: getLocalizedPath('/pricing'), icon: Tag, label: t('public_nav.pricing', 'Preise'),
    aktiv: pfad === '/pricing' || pfad.endsWith('/pricing') };
  const community = { to: getLocalizedPath('/community-backtests'), icon: BarChart3,
    label: t('public_nav.leaderboard', 'Community-Backtests'),
    // Am Telefon passt der Eigenname nicht in eine von sechs Spalten
    // (vier Zeilen bei 375 px) — dort steht die Kurzform.
    kurz: t('public_nav.leaderboard_kurz', 'Community'),
    aktiv: pfad.includes('/community-backtests') };

  const reiterLink = ({ to, icon: Icon, label, kurz, aktiv }) => (
    <Link
      key={to}
      to={to}
      className="kapsel__reiter ptn-tab"
      aria-current={aktiv ? 'page' : undefined}
      aria-label={kurz ? label : undefined}
    >
      <Icon size={18} aria-hidden="true" />
      {kurz ? (
        <>
          <span className="ptn-lang" aria-hidden="true">{label}</span>
          <span className="ptn-kurz" aria-hidden="true">{kurz}</span>
        </>
      ) : label}
    </Link>
  );

  return (
    <div className="ptn-root">
      {/* Glas-Kapsel (Liquid Design V2): die Bereichsleiste ist eine Steuerfläche,
          der aktive Reiter liegt als weisse Linse darin. */}
      <nav className="ptn-inner" style={{ maxWidth: 1280, margin: '0 auto', padding: '20px 20px 4px' }}>
        <div className="kapsel glas ptn-kapsel">
          {reiter.map(reiterLink)}
          <div className="ptn-fuerwen" ref={menuRef}>
            <button
              type="button"
              className="kapsel__reiter ptn-tab ptn-fuerwen-knopf"
              aria-expanded={menuOffen}
              aria-controls={menuId}
              aria-current={zielgruppeAktiv ? 'page' : undefined}
              onClick={() => setMenuOffen((o) => !o)}
            >
              <Users size={18} aria-hidden="true" />
              <span>{t('public_nav.fuer_wen', 'Für wen')}</span>
              <ChevronDown size={14} aria-hidden="true" className="ptn-fuerwen-pfeil" />
            </button>
            {/* Immer im HTML, nur ausgeblendet (`hidden`): Crawler ohne
                JavaScript klappen kein Menü auf. Bis 25.10.2026 entstanden
                die Einträge erst beim Klick — die vier Zielgruppenseiten
                hatten von der Startseite aus keinen einzigen Link, der
                Altersvorsorgedepot-Rechner wurde nur von zwei Seiten verlinkt.
                `hidden` nimmt die Einträge auch aus der Tab-Reihenfolge. */}
            <ul id={menuId} className="ptn-menu" hidden={!menuOffen}>
              {zielgruppen.map((z) => (
                <li key={z.pfad}>
                  <Link
                    to={getLocalizedPath(z.pfad)}
                    className="ptn-menu-eintrag"
                    aria-current={pfad.endsWith(z.pfad) ? 'page' : undefined}
                  >
                    <span className="ptn-menu-titel">{t(z.titelKey, z.titelDe)}</span>
                    <span className="ptn-menu-text">{t(z.textKey, z.textDe)}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          {reiterLink(preise)}
          {reiterLink(community)}
        </div>
      </nav>

      <style>{`
        /* Die ganze Leiste liegt auf der Menü-Ebene: Die Glas-Kapsel ist ein
           eigener Stapelkontext (backdrop-filter), der z-index des Menüs gilt
           nur darin — ohne diese Zeile malte eine später folgende Karte mit
           position: relative über das offene Menü (Preisseite, 08.10.2026). */
        .ptn-root { position: relative; z-index: var(--ebene-menue); }
        /* Die Leiste liegt auf dem Seitengrund und gehört damit optisch zum
           Kopfbereich darunter (der ebenfalls --bg-secondary trägt), nicht zum
           weissen Seitenkopf darüber — und steht mittig. */
        .ptn-root { background: var(--bg-secondary); }
        .ptn-inner { display: flex; justify-content: center; }
        .ptn-fuerwen { position: relative; display: flex; }
        .ptn-kurz { display: none; }
        .ptn-fuerwen-knopf { border: none; background: transparent; cursor: pointer; font-family: inherit; }
        .ptn-fuerwen-knopf[aria-expanded="true"] .ptn-fuerwen-pfeil { transform: rotate(180deg); }
        .ptn-menu {
          position: absolute; top: calc(100% + 8px); right: 0; z-index: 60;
          width: min(320px, calc(100vw - 20px)); margin: 0; padding: 6px; list-style: none;
          background: var(--bg-primary); border: 1px solid var(--border);
          border-radius: var(--radius-lg); box-shadow: var(--shadow-popover);
        }
        .ptn-menu-eintrag {
          display: flex; flex-direction: column; gap: 2px; padding: 10px 12px;
          border-radius: var(--radius-sm); text-decoration: none;
        }
        .ptn-menu-eintrag:hover, .ptn-menu-eintrag:focus-visible,
        .ptn-menu-eintrag[aria-current="page"] { background: var(--bg-secondary); }
        .ptn-menu-titel { font-weight: 600; color: var(--text-primary); }
        .ptn-menu-text { font-size: 1rem; color: var(--text-secondary); }
        /* Am Telefon zwei Zeilen aus je drei gleich breiten Reitern. Bis
           25.10.2026 stand hier EINE Zeile aus sechs Reitern mit 11,2 px
           Beschriftung; mit der kleinsten
           Schrift der Flotte (14 px, FLEET_DESIGN_SPEC „Typografie“) stiessen
           die Wörter aneinander („WatchlistBacktest“). Gemessen bei 375 px:
           zwei Zeilen à drei sind 91 px hoch, die alte eine Zeile mit dem
           Symbol über der Beschriftung war es 93 px — der erste Kaufknopf der
           Preisseite rückt nicht nach unten. Seit 26.10.2026 (kleinste
           Schrift 16 px) passt „Monte Carlo“ neben dem Symbol nicht mehr in
           eine von drei Spalten — das Symbol steht über der Beschriftung,
           die Leiste ist 109 statt 91 px hoch. */
        @media (max-width: 560px) {
          .ptn-inner { padding: 12px 6px 4px !important; }
          /* Raster statt Flex: Flex verteilt ab Grösse 0 nur den Rest, der
             Innenabstand der Links kam obendrauf — das Menü (ein Container
             ohne Innenabstand) war 8 px schmaler. Spalten aus dem Raster sind
             gleich breit, egal wie viel Innenabstand ein Reiter trägt. */
          .ptn-kapsel {
            width: 100%; display: grid; grid-template-columns: repeat(3, minmax(0, 1fr));
            padding: 2px; gap: 1px; border-radius: var(--radius-xl);
          }
          .ptn-kapsel > * { min-width: 0; }
          .ptn-tab {
            flex-direction: column; justify-content: center; gap: 2px; height: auto;
            padding: 6px 2px; font-size: 1rem; line-height: 1.2; border-radius: var(--radius-lg);
            white-space: normal; text-align: center; overflow-wrap: break-word;
          }
          .ptn-fuerwen-pfeil { display: none; }
          .ptn-lang { display: none; }
          .ptn-kurz { display: inline; }
          .ptn-fuerwen { position: static; }
          .ptn-fuerwen > .ptn-tab { width: 100%; }
          .ptn-inner { position: relative; }
          .ptn-menu { left: 10px; right: 10px; width: auto; top: calc(100% + 4px); }
        }
      `}</style>
    </div>
  );
}
